import { FC, forwardRef, ReactElement, ReactNode, useEffect, useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import cn from 'classnames';

import { Button } from '../button/Button';
import { Container, ContainerProps } from '../../atoms/container/Container';

import './Modal.scss';

interface WithClassNames {
  classNames?: cn.Argument;
}

// Modal.Root
export type ModalRootProps = RadixDialog.DialogProps;
export const ModalRoot: FC<ModalRootProps> = ({ children, ...props }) => (
  <RadixDialog.Root {...props}>{children}</RadixDialog.Root>
);

// Modal.Trigger
export type ModalTriggerProps = RadixDialog.DialogTriggerProps & WithClassNames;
export const ModalTrigger: FC<ModalTriggerProps> = ({ children, classNames, ...props }) => (
  <RadixDialog.Trigger {...props} className={cn('lsux-modal__trigger', classNames)}>
    {children}
  </RadixDialog.Trigger>
);

// Modal.Portal
export type ModalPortalProps = RadixDialog.DialogPortalProps;
export const ModalPortal: FC<ModalPortalProps> = ({ children, ...props }) => (
  <RadixDialog.Portal {...props}>{children}</RadixDialog.Portal>
);

// Modal.Overlay
export type ModalOverlayProps = RadixDialog.DialogOverlayProps & WithClassNames;
export const ModalOverlay: FC<ModalOverlayProps> = forwardRef<HTMLDivElement, ModalOverlayProps>(
  ({ children, classNames, ...props }, ref) => (
    <RadixDialog.Overlay ref={ref} {...props} className={cn('lsux-modal__overlay', classNames)}>
      {children}
    </RadixDialog.Overlay>
  ),
);
ModalOverlay.displayName = 'ModalOverlay';

// Modal.Content
export type ModalContentProps = RadixDialog.DialogContentProps & WithClassNames;
export const ModalContent: FC<ModalContentProps> = forwardRef<HTMLDivElement, ModalContentProps>(
  ({ children, classNames, ...props }, ref) => (
    <RadixDialog.Content ref={ref} {...props} className={cn('lsux-modal__content', classNames)}>
      {children}
    </RadixDialog.Content>
  ),
);
ModalContent.displayName = 'ModalContent';

// Modal.Title
export interface ModalTitleProps extends RadixDialog.DialogTitleProps, WithClassNames {
  /**
   * Children components for the title.
   * @type {string}
   */
  children?: string;
  /**
   * The text for the title of the dialog.
   * @type {string}
   */
  text?: string;
}
export const ModalTitle: FC<ModalTitleProps> = ({ children, classNames, text }) => (
  <RadixDialog.Title className={cn('lsux-modal__title', 'mb-4', classNames)}>{children ?? text}</RadixDialog.Title>
);

// Modal.Description
export type ModalDescriptionProps = RadixDialog.DialogDescriptionProps & WithClassNames;
export const ModalDescription: FC<ModalDescriptionProps> = ({ children, classNames, ...props }) => (
  <RadixDialog.Description {...props} className={cn('lsux-modal__description', classNames)}>
    {children}
  </RadixDialog.Description>
);

// Modal.Close
export type ModalCloseProps = RadixDialog.DialogCloseProps;
export const ModalClose: FC<ModalCloseProps> = ({ children, ...props }) => (
  <RadixDialog.Close {...props}>{children}</RadixDialog.Close>
);

export interface ModalActionsProps extends ContainerProps {
  /**
   * Children components
   */
  children?: string | number | ReactElement | ReactElement[];
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
}

export const ModalActions: FC<ModalActionsProps> = ({
  children,
  classNames = ['lsux-modal__button-bar'],
  ...props
}: ModalActionsProps) => {
  return (
    <Container {...props} classNames={classNames}>
      {children as ReactElement}
    </Container>
  );
};

export interface ModalProps extends ModalRootProps {
  /**
   * The children of the modal.
   */
  children?: ReactNode | ReactNode[];
  /**
   * Text for the title of the dialog.
   */
  title?: string;
  /**
   * Indicates the modal should have a close button.
   */
  closeButton?: boolean;
  /**
   * Additional classnames on the modal
   */
  classNames?: cn.Argument;
  /**
   * Function called when the close button is pressed
   */
  closeFunction?: () => void;
  /**
   * Makes the modal full size on the screen
   * @default false
   */
  isFullScreen?: boolean;
  /**
   * Position of the modal. Either `top`, `center`, or `bottom`.
   * @default top
   */
  position?: 'top' | 'center' | 'bottom';
  /**
   * Defines the max-height of the modal.
   * @default 95%
   */
  maxHeight?: string;
  /**
   * Defines the max-width of the modal.
   * @default 335px
   */
  maxWidth?: string;
  /**
   * Element that triggers [data-state='open'] and opens modal.
   */
  trigger?: ReactNode;
  /**
   * Props for the content of the modal.
   */
  contentProps?: ModalContentProps;
}

const ModalComponent = ({
  children,
  classNames = [],
  closeButton,
  closeFunction,
  contentProps,
  title,
  isFullScreen = false,
  position = 'top',
  maxHeight = '95%',
  maxWidth = '335px',
  open,
  trigger,
  ...props
}: ModalProps) => {
  const [openState, setOpenState] = useState(!trigger ? true : open);

  const classModalContent = 'lsux-modal__content';
  const cnModalContent = cn(
    {
      [`${classModalContent}--full`]: isFullScreen,
      [`${classModalContent}--position-${position}`]: position !== 'top',
      ['p-5']: true,
      ['pt-4']: !(closeButton && closeFunction),
      ['pt-6']: closeButton && closeFunction,
    },
    classNames,
  );

  useEffect(() => {
    document.documentElement.style.setProperty('--modal-max-height', maxHeight);
    document.documentElement.style.setProperty('--modal-max-width', maxWidth);
  }, [maxHeight, maxWidth]);

  function handleOpenChange(open: boolean) {
    setOpenState(open);
    props.onOpenChange?.(open);
  }

  return (
    <ModalRoot open={openState} onOpenChange={handleOpenChange} {...props}>
      {trigger}
      <ModalPortal>
        <ModalOverlay />
        <ModalContent
          classNames={cnModalContent}
          onEscapeKeyDown={closeFunction}
          onInteractOutside={closeFunction}
          {...contentProps}
        >
          {closeButton ? (
            <div className="lsux-modal__header">
              <ModalClose asChild onClick={closeFunction}>
                <Button
                  iconLeft="edit_close_circle_filled"
                  iconSize="medium"
                  shape="rectangular"
                  variant="tertiary"
                  alt="Close"
                  classNames={['lsux-modal__close-button']}
                />
              </ModalClose>
            </div>
          ) : null}
          <ModalTitle>{title}</ModalTitle>
          {children}
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
};

ModalComponent.displayName = 'Modal';

type ModalComp = FC<ModalProps> & {
  // Radix components
  Root: FC<ModalRootProps>;
  Trigger: FC<ModalTriggerProps>;
  Portal: FC<ModalPortalProps>;
  Overlay: FC<ModalOverlayProps>;
  Content: FC<ModalContentProps>;
  Description: FC<ModalDescriptionProps>;
  Close: FC<ModalCloseProps>;
  // Custom components
  Actions: FC<ModalActionsProps>;
  Title: FC<ModalTitleProps>;
};

/**
 * Modal is an overlaying content container above everything else on the screen.
 * It captures attention by disrupting workflow and blocking user's access to other content area, so use sparingly.
 */
export const Modal = ModalComponent as ModalComp;

Modal.Root = ModalRoot;
Modal.Trigger = ModalTrigger;
Modal.Portal = ModalPortal;
Modal.Overlay = ModalOverlay;
Modal.Content = ModalContent;
Modal.Description = ModalDescription;
Modal.Close = ModalClose;
Modal.Actions = ModalActions;
Modal.Title = ModalTitle;
